import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Dimensions,
  ActivityIndicator,
  Modal,
  Image,
  Animated
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from "expo-constants";
import { Ionicons, FontAwesome5 } from '@expo/vector-icons';
import { authenticate, testLoginFlow, debugAsyncStorage, getCaptcha, checkCaptcha } from '../services/authApi';
import { API_BASE_URL } from '../config/api';
import { useCustomAlertModal } from '../components/CustomAlertModal';
import TurnstileWidget from '../components/TurnstileWidget';

const { width, height } = Dimensions.get('window');

export default function LoginScreen({ navigation }) {
  const { 
    customAlertModal, 
    showCustomAlert, 
    hideCustomAlert, 
    handleCustomAlertConfirm
  } = useCustomAlertModal();
  const [formData, setFormData] = useState({
    emailOrPhone: '',
    password: '',
    captcha: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captcha, setCaptcha] = useState({url: null, loading: true, token: '', error: false});
  
  const [googleButtonClicked, setGoogleButtonClicked] = useState(false);
  const [facebookButtonClicked, setFacebookButtonClicked] = useState(false);
  const [appleButtonClicked, setAppleButtonClicked] = useState(false);

  const [captchaToken, setCaptchaToken] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const { emailOrPhone, password, captcha } = formData;
    
    // console.log('Validating form:', { emailOrPhone, password });
    
    if (!emailOrPhone.trim()) {
      console.log('Validation failed: Email/phone is empty');
      showCustomAlert('Error', 'Email atau nomor telepon tidak boleh kosong');
      return false;
    }
    
    if (!password.trim()) {
      console.log('Validation failed: Password is empty');
      showCustomAlert('Error', 'Password tidak boleh kosong');
      return false;
    }
    
    if (password.length < 6) {
      console.log('Validation failed: Password too short');
      showCustomAlert('Error', 'Password minimal 6 karakter');
      return false;
    }
    
    if (captcha.length < 6) {
      console.log('Validation failed: Captcha is empty');
      showCustomAlert('Error', 'Captcha tidak boleh kosong');
      return false;
    }
    
    console.log('Form validation passed');
    return true;
  };

  const handleLogin = async () => {
    console.log('handleLogin called');
    
    if (!validateForm()) {
      console.log('Form validation failed, returning early');
      return;
    }

    console.log('Setting loading to true');
    setIsLoading(true);
    
    try {
      console.log('=== DEBUGGING LOGIN FLOW ===');
      // check captcha
      console.log('Calling checkCaptcha');
      const checkCaptchaResult = await checkCaptcha(formData.captcha, captcha.token);
      if(!checkCaptchaResult.success) {
        loadCaptcha();
        handleInputChange('captcha', '');
        throw new Error(checkCaptchaResult.message);
      }

      console.log('Before login - AsyncStorage state:');
      await debugAsyncStorage();
      
      console.log('Calling testLoginFlow');
      
      // Use testLoginFlow for comprehensive debugging
      const testResult = await testLoginFlow({
        'email-or-telp': formData.emailOrPhone,
        'password': formData.password,
        'captcha-token': captchaToken,
      });
      
      if (!testResult.success) {
        throw new Error(testResult.error);
      }
      
      const response = testResult.result;
      console.log('Login test completed successfully');
      
      // Store user data and userToken for app navigation
      // Note: session_id and remember_token are already stored by authenticate function
      // console.log('Storing userData from login response:', {
      //   hasId: !!response.data.id,
      //   hasUser: !!response.data.user,
      //   hasUserData: !!response.data.userData,
      //   dataKeys: Object.keys(response.data)
      // });
      await AsyncStorage.setItem('userData', JSON.stringify(response.data));
      await AsyncStorage.setItem('userToken', response.data.remember_token || 'authenticated');
      
      console.log('After login - AsyncStorage state:');
      await debugAsyncStorage();
      
      // Show success message
      showCustomAlert(
        'Login Berhasil', 
        response.message || 'Selamat datang! Login berhasil.',
        'success'
      );
      
      // Navigate after a short delay
      setTimeout(() => {
        console.log('Login successful, forcing navigation refresh');
        if (Platform.OS === 'web' && typeof window !== 'undefined' && window.forceAuthCheck) {
          console.log('Calling forceAuthCheck for web');
          window.forceAuthCheck();
        } else {
          console.log('Mobile platform, auth state will be detected automatically');
        }
      }, 1500);
      
    } catch (error) {
      console.log('Login error caught:', error);
      console.log('Error message:', error.message);

      // // reload captcha
      // loadCaptcha();
      // setFormData(prev => ({ ...prev, captcha: '' }));
      
      const errorMessage = error.message || 'Login gagal. Silakan coba lagi.';
      console.log('Showing custom alert with message:', errorMessage);
      
      showCustomAlert('Login Gagal', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigation.navigate('ForgotPassword');
  };

  const handleRegister = () => {
    navigation.navigate('Register');
  };

  const handleGoogleLogin = async () => {
    // save this last page to storage, to handle failed auth callback
    await AsyncStorage.setItem('auth_caller', 'Login'); 

    setGoogleButtonClicked(true);

    const formData = new FormData();
    formData.append('login-from', "ZCLUB");
    formData.append('src-member', "mobile_app");
    formData.append('captcha-token', captchaToken);
    
    await fetch(`${API_BASE_URL}/api/auth/google/login`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
      body: formData,
    })
    .then(response => response.json())
    .then(result => {
      if( !result.success ) {
        throw new Error( result.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi' );
      }

      if( !!result.auth_url && URL.canParse(result.auth_url) ) {
        window.location.replace( result.auth_url );
      }
    })
    .catch(e => {
      console.log( e );
      setGoogleButtonClicked(false);
      showCustomAlert('Error', e.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi');
    });
  };

  const handleFacebookLogin = async () => {
    // save this last page to storage, to handle failed auth callback
    await AsyncStorage.setItem('auth_caller', 'Login'); 

    setFacebookButtonClicked(true);

    const formData = new FormData();
    formData.append('login-from', "ZCLUB");
    formData.append('src-member', "mobile_app");
    formData.append('captcha-token', captchaToken);
    
    await fetch(`${API_BASE_URL}/api/auth/facebook/login`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
      body: formData,
    })
    .then(response => response.json())
    .then(result => {
      if( !result.success ) {
        throw new Error( result.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi' );
      }

      if( !!result.auth_url && URL.canParse(result.auth_url) ) {
        window.location.replace( result.auth_url );
      }
    })
    .catch(e => {
      console.log( e );
      setFacebookButtonClicked(false);
      showCustomAlert('Error', e.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi');
    });
  };

  const handleAppleLogin = async () => {
    // save this last page to storage, to handle failed auth callback
    await AsyncStorage.setItem('auth_caller', 'Login'); 

    setAppleButtonClicked(true);

    const formData = new FormData();
    formData.append('login-from', "ZCLUB");
    formData.append('src-member', "mobile_app");
    formData.append('captcha-token', captchaToken);
    
    await fetch(`${API_BASE_URL}/api/auth/apple/login`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
      body: formData,
    })
    .then(response => response.json())
    .then(result => {
      if( !result.success ) {
        throw new Error( result.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi' );
      }

      if( !!result.auth_url && URL.canParse(result.auth_url) ) {
        window.location.replace( result.auth_url );
      }
    })
    .catch(e => {
      console.log( e );
      setAppleButtonClicked(false);
      showCustomAlert('Error', e.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi');
    });
  };

  const loadCaptcha = async () => {
    setCaptcha({url: null, loading: true, token: '', error: false});
    
    try {
      const response = await getCaptcha();
      const imageUrl = response.captcha_url || response.captcha;

      if(imageUrl) {
        setCaptcha({
          url: imageUrl,
          loading: false,
          token: response.captcha_token || '',
          error: false,
        });
      } else {
        console.log('Failed to load captcha: ', response.error);
        setCaptcha({url: null, loading: false, token: '', error: true});
      }
    } catch (error) {
      console.log('Error loading captcha: ', error);
      setCaptcha({url: null, loading: false, token: '', error: true});
    }
  }

  const SpinView = props => {
    const spinAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
      Animated.loop(
        Animated.timing(spinAnim, {
          toValue: 1,
          duration: 1000,
        }),
        {
          iterations: -1
        }
      ).start();
    }, [spinAnim]);

    // map
    const spinDeg = spinAnim.interpolate({
      inputRange: [0, 1],
      outputRange: ['0deg', '360deg'],
    })

    return (
      <Animated.View
        style={{
          ...props.style,
          transform: [{
            rotate: spinDeg
          }]
        }}
      >
        {props.children}
      </Animated.View>
    )
  }

  useFocusEffect(
    React.useCallback(()=>{
      // check pending phone from auth by phone
      async function checkPendingPhoneAuth() {
        const pendingPhone = await AsyncStorage.getItem('pendingPhone');
        if(pendingPhone) {
          handleInputChange('emailOrPhone', pendingPhone);

          await AsyncStorage.removeItem('pendingPhone');
        }
      }

      async function checkAuthErrorMessage() {
        const authErrorMessage = await AsyncStorage.getItem('auth_error_message');
        if( !!authErrorMessage ) {
          showCustomAlert('Error', authErrorMessage || 'Terjadi kesalahan, silakan coba beberapa saat lagi');
          await AsyncStorage.removeItem('auth_error_message');
        }
      }

      checkAuthErrorMessage();
      loadCaptcha();
      checkPendingPhoneAuth();
    }, [navigation])
  );


  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.container}>
        <ScrollView 
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.header}>
            <TouchableOpacity 
              style={styles.backButton}
              onPress={() => navigation.goBack()}
            >
              <Ionicons name="arrow-back" size={24} color="#000000" />
            </TouchableOpacity>
          </View>

          <View style={styles.content}>
            <View style={styles.titleSection}>
              <Text style={styles.title}>Masuk</Text>
              <Text style={styles.subtitle}>
                Selamat datang! Masuk untuk lihat dan tukar point yang kamu kumpulkan
              </Text>
            </View>

            <View style={styles.formSection}>
              {/* Email/Phone Input */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>No. Hp atau Email</Text>
                <View style={styles.inputContainer}>
                  <TextInput
                    style={styles.input}
                    placeholder="No. HP atau Email"
                    placeholderTextColor="#94A3B8"
                    value={formData.emailOrPhone}
                    onChangeText={(value) => handleInputChange('emailOrPhone', value)}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                </View>
              </View>

              {/* Password Input */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Password</Text>
                <View style={styles.inputContainer}>
                  <TextInput
                    style={styles.input}
                    placeholder="********"
                    placeholderTextColor="#94A3B8"
                    value={formData.password}
                    onChangeText={(value) => handleInputChange('password', value)}
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity 
                    onPress={() => setShowPassword(!showPassword)}
                    style={styles.eyeIcon}
                  >
                    <Ionicons 
                      name={showPassword ? "eye-outline" : "eye-off-outline"} 
                      size={20} 
                      color="#64748B" 
                    />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Captcha */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Captcha</Text>
                <View style={styles.inputGroup}>
                  <View style={{
                    ...styles.inputContainer,
                    flex: 1,
                  }}>
                    <TextInput
                      style={{
                        ...styles.input,
                        width: '100%',
                      }}
                      placeholder="Captcha"
                      placeholderTextColor="#94A3B8"
                      value={formData.captcha}
                      onChangeText={(value) => handleInputChange('captcha', value)}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                  </View>
                  {captcha.url !== null && (
                    Platform.OS === 'web' ? (
                      <img
                        src={captcha.url}
                        alt="Captcha"
                        width={130}
                        height={48}
                        onError={() => setCaptcha(prev => ({ ...prev, error: true, url: null }))}
                        style={{
                          width: 130,
                          height: 48,
                          objectFit: 'contain',
                          flexShrink: 0,
                          display: 'block',
                          backgroundColor: '#F8FAFC',
                        }}
                      />
                    ) : (
                      <Image
                        style={styles.captcha}
                        source={{uri: captcha.url}}
                        resizeMode='contain'
                      />
                    )
                  )}
                  {captcha.error && captcha.url === null && !captcha.loading && (
                    <Text style={{ fontSize: 11, color: '#EF4444', width: 72 }}>Gagal muat</Text>
                  )}
                  <TouchableOpacity 
                    onPress={loadCaptcha}
                    style={styles.eyeIcon}
                  >
                    {captcha.loading ? (
                      <SpinView>
                        <Ionicons 
                          name={"sync"} 
                          size={20} 
                          color="#1E293B" 
                        />
                      </SpinView>
                    ) : (
                      <View>
                        <Ionicons 
                          name={"sync"} 
                          size={20} 
                          color="#1E293B" 
                        />
                      </View>
                    )}
                  </TouchableOpacity>
                </View>
              </View>

              {/* Forgot Password */}
              <TouchableOpacity 
                style={styles.forgotPasswordButton}
                onPress={handleForgotPassword}
              >
                <Text style={styles.forgotPasswordText}>Lupa Password?</Text>
              </TouchableOpacity>

              {/* Login Button */}
              <TouchableOpacity 
                style={[styles.loginButton, styles.externalAuthButton, isLoading && styles.disabledButton]} 
                onPress={handleLogin}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator color="white" size="small" />
                ) : (
                  <Text style={styles.loginButtonText}>Masuk</Text>
                )}
              </TouchableOpacity>

              <Text style={styles.extrenalAuthDivider}>atau masuk dengan</Text>

              <TouchableOpacity
                style={[styles.loginButton, styles.externalAuthButton, styles.googleButton, (isLoading || googleButtonClicked) && styles.disabledButton]}
                onPress={handleGoogleLogin}
                disabled={isLoading || googleButtonClicked}
              >
                <FontAwesome5 style={styles.externalAuthButtonIcon} name="google" size={14} color="#000000" />
                <Text style={[styles.loginButtonText, styles.googleButtonText]}>Masuk dengan Google</Text>
              </TouchableOpacity>

              {/*<TouchableOpacity
                style={[styles.loginButton, styles.externalAuthButton, styles.facebookButton, (isLoading || facebookButtonClicked) && styles.disabledButton]}
                onPress={handleFacebookLogin}
                disabled={isLoading || facebookButtonClicked}
              >
                <FontAwesome5 style={styles.externalAuthButtonIcon} name="facebook" size={14} color="#FFFFFF" />
                <Text style={styles.loginButtonText}>Masuk dengan Facebook</Text>
              </TouchableOpacity>*/}

              <TouchableOpacity
                style={[styles.loginButton, styles.externalAuthButton, styles.appleButton, (isLoading || appleButtonClicked) && styles.disabledButton]}
                onPress={handleAppleLogin}
                disabled={isLoading || appleButtonClicked}
              >
                <FontAwesome5 style={styles.externalAuthButtonIcon} name="apple" size={14} color="#FFFFFF" />
                <Text style={styles.loginButtonText}>Masuk dengan Apple</Text>
              </TouchableOpacity>

              {/* Register Link */}
              <View style={styles.registerContainer}>
                <Text style={styles.registerText}>Belum punya akun? </Text>
                <TouchableOpacity onPress={handleRegister}>
                  <Text style={styles.registerLink}>Daftar Sekarang</Text>
                </TouchableOpacity>
              </View>

              {/* Turnstile renders only on web */}
              {Platform.OS === 'web' && (
                <TurnstileWidget
                  siteKey={Constants.expoConfig?.extra?.TURNSTILE_SITE_KEY}
                  onVerify={(token) => setCaptchaToken(token)}
                />
              )}
            </View>
          </View>
        </ScrollView>
      </View>
      
      {customAlertModal}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollContent: {
    flexGrow: 1,
    padding: 16,
    paddingTop: 32,
  },
  header: {
    marginBottom: 32,
  },
  backButton: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  titleSection: {
    marginBottom: 32,
  },
  title: {
    fontSize: 30,
    lineHeight: 36,
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    color: '#00000',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 24,
    fontFamily: 'Poppins',
    color: '#64748B',
  },
  formSection: {
    flex: 1,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: '#374151',
    marginBottom: 8,
    fontFamily: 'Poppins-Medium',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#AAAAAA',
    paddingHorizontal: 16,
    height: 48,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
    fontFamily: 'Poppins',
  },
  eyeIcon: {
    padding: 4,
  },
  forgotPasswordButton: {
    alignSelf: 'flex-end',
    marginBottom: 24,
  },
  forgotPasswordText: {
    fontSize: 14,
    color: '#1E293B',
    fontWeight: 500,
    fontFamily: 'Poppins-Medium'
  },
  loginButton: {
    backgroundColor: '#000000',
    borderRadius: 6,
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  disabledButton: {
    opacity: 0.7,
  },
  loginButtonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 20,
    color: '#FFFFFF',
  },
  extrenalAuthDivider: {
    width: '100%', 
    textAlign: 'center', 
    fontSize: 12, 
    fontFamily: 'Poppins', 
    color: '#94A3B8',
    marginBottom: 16,
  },
  externalAuthButton: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  externalAuthButtonIcon: {
    width: 14, 
    justifyContent: 'center',
  },
  googleButton: {
    backgroundColor: '#FFFFFF',
    borderColor: '#9CA3AF',
    borderWidth: 1,
  },
  googleButtonText: {
    color: '#000000',
  },
  facebookButton: {
    backgroundColor: '#2563eb',
  },
  appleButton: {
    backgroundColor: '#0f172a',
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#fff',
  },
  dividerText: {
    color: '#64748B',
    fontSize: 14,
    marginHorizontal: 16,
  },
  googleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    height: 48,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  googleButtonText: {
    color: '#1E293B',
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 8,
  },
  registerContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  registerText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: 400,
    color: '#64748B',
  },
  registerLink: {
    fontSize: 14,
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    color: '#1E293B',
  },
  captchaContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  captcha: {
    flex: 1,
    minHeight: 48,
    maxHeight: 48,
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
});
